import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActionPanel = ({ analysis = null, jobPosting = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [progressData, setProgressData] = useState({
    totalAnalyses: 0,
    skillsIdentified: 0,
    areasToImprove: 0,
    profileCompleteness: 0
  });

  // Fetch progress data from Supabase
  useEffect(() => {
    if (user?.id) {
      fetchProgressData();
    }
  }, [user?.id]);

  const fetchProgressData = async () => {
    try {
      const userId = user?.id;

      // Get total analyses count
      const { count: analysesCount } = await supabase
        .from('resume_analyses')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'completed');

      // Get total skills identified
      const { data: analyses } = await supabase
        .from('resume_analyses')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'completed');

      const analysisIds = analyses?.map(a => a.id) || [];

      // Only query if we have analyses (prevent 400 error with empty array)
      let skillsCount = 0;
      let gapsCount = 0;

      if (analysisIds.length > 0) {
        const { count: skills } = await supabase
          .from('resume_skills')
          .select('*', { count: 'exact', head: true })
          .in('resume_analysis_id', analysisIds);

        // Get total skill gaps
        const { count: gaps } = await supabase
          .from('skill_gaps')
          .select('*', { count: 'exact', head: true })
          .in('resume_analysis_id', analysisIds);

        skillsCount = skills || 0;
        gapsCount = gaps || 0;
      }

      // Calculate profile completeness
      const completeness = analysesCount > 0 ? Math.min(100, Math.round(
        ((analysesCount > 0 ? 30 : 0) +
          (skillsCount > 0 ? 40 : 0) +
          (gapsCount > 0 ? 30 : 0))
      )) : 0;

      setProgressData({
        totalAnalyses: analysesCount || 0,
        skillsIdentified: skillsCount || 0,
        areasToImprove: gapsCount || 0,
        profileCompleteness: completeness
      });
    } catch (error) {
      console.error('Error fetching progress data:', error);
    }
  };

  const handleUpgrade = () => {
    // Implement upgrade logic with Polar integration
    console.log('Redirecting to upgrade...');
    setIsUpgradeModalOpen(false);
  };

  // Generate shareable content
  const generateShareText = () => {
    const matchScore = analysis?.job_match_score || analysis?.overall_resume_score || 0;
    const jobTitle = jobPosting?.title || 'my dream job';
    const platformUrl = 'https://aspirant-ai.com';

    const baseText = `🎯 Just analyzed my resume for ${jobTitle} using JobAspirant!`;
    const scoreText = `\n\n📊 My Job Fit Score: ${matchScore}%`;
    const platformText = `\n\n✨ JobAspirant uses real-time job market data & AI to help you:\n• Identify skill gaps\n• Get personalized learning roadmaps\n• Match your resume with live job requirements\n\n🚀 Try it free: ${platformUrl}`;

    return `${baseText}${scoreText}${platformText}`;
  };

  const handleShare = (platform) => {
    const shareText = generateShareText();
    const encodedText = encodeURIComponent(shareText);
    const platformUrl = 'https://aspirant-ai.com';

    let shareUrl = '';

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedText}`;
        window.open(shareUrl, '_blank');
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        window.open(shareUrl, '_blank');
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(platformUrl)}&quote=${encodedText}`;
        window.open(shareUrl, '_blank');
        break;
      case 'native':
        if (navigator.share) {
          navigator.share({
            title: 'JobAspirant - Live Job Fit Analysis',
            text: shareText,
            url: platformUrl
          }).catch(() => { });
        }
        break;
      default:
        break;
    }

    setIsShareMenuOpen(false);
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Actions</h3>
            <p className="text-sm text-muted-foreground">Quick actions for your analysis</p>
          </div>

          {/* Secondary Actions */}
          <div className="space-y-3">
            <h4 className="font-medium text-foreground">Other Actions</h4>

            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              fullWidth
              disabled={user?.tier === 'free' && (user?.creditsRemaining || 0) === 0}
              onClick={() => navigate('/resume-upload-analysis')}
            >
              New Analysis
            </Button>

            <div className="relative">
              <Button
                variant="ghost"
                iconName="Share"
                iconPosition="left"
                fullWidth
                onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
              >
                Share Results
              </Button>

              {/* Share Menu Dropdown */}
              {isShareMenuOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-10 p-3 space-y-2">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <Icon name="MessageCircle" size={20} className="text-success" />
                    <span className="text-sm font-medium text-foreground">Share on WhatsApp</span>
                  </button>

                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <Icon name="Twitter" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Share on X (Twitter)</span>
                  </button>

                  {navigator.share && (
                    <button
                      onClick={() => handleShare('native')}
                      className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                      <Icon name="Share2" size={20} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">More options...</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats for Free Users */}
          {user?.tier === 'free' && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Info" size={20} className="text-warning" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">Free Tier Usage</div>
                  <div className="text-xs text-muted-foreground">
                    {user?.creditsRemaining || 0} analysis remaining • {progressData.totalAnalyses} completed
                  </div>
                </div>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setIsUpgradeModalOpen(true)}
                  iconName="Crown"
                  iconPosition="left"
                >
                  Upgrade
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Upgrade Modal */}
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
          <div className="bg-card rounded-lg shadow-prominent max-w-md w-full animate-scale-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-card-foreground">Unlock Premium Features</h3>
                <button
                  onClick={() => setIsUpgradeModalOpen(false)}
                  className="p-1 hover:bg-muted rounded-md transition-colors duration-200"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-center">
                  <Icon name="Crown" size={48} className="text-warning mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">
                    Get unlimited access to advanced career development tools
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Unlimited resume analyses</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Personalized learning paths</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">AI-powered resume rewriter</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Advanced skill gap analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Priority support</span>
                  </div>
                </div>

                <div className="text-center py-4">
                  <div className="text-2xl font-bold text-card-foreground">₹999</div>
                  <div className="text-sm text-muted-foreground">per month</div>
                  <div className="text-xs text-muted-foreground mt-1">Cancel anytime</div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsUpgradeModalOpen(false)}
                    className="flex-1"
                  >
                    Maybe Later
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleUpgrade}
                    className="flex-1"
                    iconName="Crown"
                    iconPosition="left"
                  >
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionPanel;